import { useCallback, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { autocompleteAddress, getAddressDetails } from "@fp/admin-api-client/addresses/addresses.client";
import type { AddressSuggestionDto } from "@fp/admin-api-client/types/addressSuggestionDto";
import type { AddressDto } from "@fp/admin-api-client/types/addressDto";
import { Input } from "@/components/base/input/input";

const generateSessionToken = () => {
	const bytes = new Uint8Array(16);
	crypto.getRandomValues(bytes);
	return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

export interface AddressAutocompleteInputProps {
	value: string;
	onChange: (value: string) => void;
	onAddressSelected: (address: AddressDto) => void;
	className?: string;
	label?: string;
	isDisabled?: boolean;
}

export const AddressAutocompleteInput = ({ value, onChange, onAddressSelected, className, label = "Address", isDisabled }: AddressAutocompleteInputProps) => {
	const [suggestions, setSuggestions] = useState<AddressSuggestionDto[]>([]);
	const [isOpen, setIsOpen] = useState(false);
	const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
	const sessionTokenRef = useRef(generateSessionToken());
	const autocompleteAbortRef = useRef<AbortController | null>(null);
	const detailsAbortRef = useRef<AbortController | null>(null);
	const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

	useEffect(() => {
		navigator.geolocation?.getCurrentPosition(
			(position) => setUserLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
			() => {},
			{ maximumAge: 600000 },
		);
	}, []);

	const fetchSuggestions = useDebouncedCallback(async (input: string) => {
		if (input.length < 3) {
			setSuggestions([]);
			setIsOpen(false);
			return;
		}

		autocompleteAbortRef.current?.abort();
		autocompleteAbortRef.current = new AbortController();

		try {
			const response = await autocompleteAddress(
				{ input, sessionToken: sessionTokenRef.current, latitude: userLocation?.latitude, longitude: userLocation?.longitude },
				{ signal: autocompleteAbortRef.current.signal },
			);
			setSuggestions(response.data.suggestions ?? []);
			setIsOpen((response.data.suggestions ?? []).length > 0);
		} catch {
			setSuggestions([]);
			setIsOpen(false);
		}
	}, 300);

	const handleSelect = useCallback(
		async (suggestion: AddressSuggestionDto) => {
			setIsOpen(false);
			setSuggestions([]);
			autocompleteAbortRef.current?.abort();
			detailsAbortRef.current?.abort();
			detailsAbortRef.current = new AbortController();

			try {
				const response = await getAddressDetails(
					suggestion.placeId,
					{ sessionToken: sessionTokenRef.current },
					{ signal: detailsAbortRef.current.signal },
				);
				onAddressSelected(response.data);
			} catch {
				onChange(suggestion.mainText);
			} finally {
				sessionTokenRef.current = generateSessionToken();
			}
		},
		[onChange, onAddressSelected],
	);

	const handleChange = useCallback(
		(v: string) => {
			detailsAbortRef.current?.abort();
			onChange(v);
			fetchSuggestions(v);
		},
		[onChange, fetchSuggestions],
	);

	const handleBlur = useCallback(() => {
		blurTimeoutRef.current = setTimeout(() => setIsOpen(false), 150);
	}, []);

	const handleFocus = useCallback(() => {
		if (suggestions.length > 0) setIsOpen(true);
	}, [suggestions]);

	const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
		if (e.key === "Escape") {
			setIsOpen(false);
			e.preventDefault();
		}
	}, []);

	return (
		<div className={`relative ${className ?? ""}`}>
			<Input
				label={label}
				value={value}
				onChange={handleChange}
				onBlur={handleBlur}
				onFocus={handleFocus}
				onKeyDown={handleKeyDown}
				autoComplete="off"
				isDisabled={isDisabled}
			/>
			{isOpen && suggestions.length > 0 && (
				<ul className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
					{suggestions.map((suggestion) => (
						<li key={suggestion.placeId}>
							<button type="button" className="w-full px-3 py-2 text-left hover:bg-gray-50 cursor-pointer" onMouseDown={(e) => { e.preventDefault(); clearTimeout(blurTimeoutRef.current); handleSelect(suggestion); }}>
								<span className="font-medium text-sm">{suggestion.mainText}</span>
								<span className="text-xs text-gray-500 ml-1">{suggestion.secondaryText}</span>
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};